import React, { FC, useEffect, useRef, useState } from 'react';
import dogIcon from 'assets/images/dog.svg';
import shelterIcon from 'assets/images/partneringShelters.svg';
import instIcon from 'assets/images/partneringInstitutions.svg';
import { useParams } from 'react-router';
import { Carousel, CarouselControl, CarouselIndicators, CarouselItem } from 'reactstrap';
import logo from 'assets/images/P4P_Logo-gray.png';
import noPhoto from 'assets/images/noPhoto.png';
import classNames from 'classnames';
import { IDogDetail } from 'types/Dog';
import { APIError, BASE_URL, DataProvider } from 'data/Provider';
import { capitalizeEveryWord } from 'helpers/capitalizeEveryWord';
import Lightbox from 'yet-another-react-lightbox';
import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import ErrorAlert from 'components/ErrorAlert';


const DogDetail: FC = () => {
    const { id } = useParams();

    const [dogDetail, setDogDetail] = useState<IDogDetail>();
    const headerRef = useRef<HTMLDivElement>();
    const carouselRef = useRef<HTMLDivElement>();
    const dogRef = useRef<HTMLDivElement>();
    const aboutRef = useRef<HTMLDivElement>();
    const shelterRef = useRef<HTMLDivElement>();
    const institutionRef = useRef<HTMLDivElement>();
    const [stickyHeader, setStickyHeader] = useState(false);
    const [aboutLinkActive, setAboutLinkActive] = useState(true);
    const [shelterLinkActive, setShelterLinkActive] = useState(false);
    const [institutionLinkActive, setIinstitutionLinkActive] = useState(false);
    const [dogPhotos, setDogPhotos] = useState([]);
    const [errorObject, setErrorObject] = useState<APIError>();
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        function dogDetailScroll() {
            if (dogRef.current) {
                let height = headerRef.current.offsetHeight + dogRef.current.offsetHeight + 70;
                let scrollY = window.scrollY;
                if (scrollY > height) {
                    setStickyHeader(true);
                } else {
                    setStickyHeader(false);
                }
                let shelterTop = shelterRef.current.offsetTop;
                let institutionTop = institutionRef.current.offsetTop;
                setAboutLinkActive(shelterTop - scrollY > window.innerHeight / 2);
                setShelterLinkActive(shelterTop - scrollY < window.innerHeight / 2 && institutionTop - scrollY >= window.innerHeight / 2);
                setIinstitutionLinkActive(institutionTop - scrollY < window.innerHeight / 2);
            }
        }
        window.addEventListener('scroll', dogDetailScroll);
        document.title = 'Dog Detail • Puppies For Parole';
        return () => window.removeEventListener('scroll', dogDetailScroll);
    }, [])



    useEffect(() => {
        retrieveDogDetail(id);
    }, [id]);




    const retrieveDogDetail = async (i: string) => {
        if (i) {
            try {
                const res = await DataProvider.get(`/dogs/${i}`);
                let dog = await res.data
                setDogDetail(dog);

            } catch (err) {
                setErrorObject(err);
            } finally {
                setLoading(false);
            }
        }
    }



    useEffect(() => {
        if (!dogDetail) return;
        let photos = dogDetail.photoIds.map((d, i) => {
            return {
                'altText': 'Dog Image' + i,
                'src': BASE_URL + '/photos/' + id,
                'key': i
            }
        })
        setDogPhotos(photos);
        setActiveIndex(0);
    }, [dogDetail, id]);




    const next = () => {
        if (animating) return;
        const nextIndex = activeIndex === dogDetail?.photoIds.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
    };

    const previous = () => {
        if (animating) return;
        const nextIndex = activeIndex === 0 ? dogDetail?.photoIds.length - 1 : activeIndex - 1;
        setActiveIndex(nextIndex);
    };

    const goToIndex = (newIndex) => {
        if (animating) return;
        setActiveIndex(newIndex);
    };

    const previousPhoto = () => {
        if (dogDetail?.photoIds.length === 1) {
            return <img src={logo} className='h-100 w-100 mx-auto img-fluid img-fluid' alt={'Dog Photo of ' + dogDetail?.name} style={{ paddingTop: '2rem' }} />
        }
        else {
            const index = activeIndex === 0 ? dogDetail?.photoIds.length - 1 : activeIndex - 1;
            return <img src={id ? (BASE_URL + '/photos/' + dogDetail?.photoIds[index]) : logo} className='h-100 mx-auto img-fluid img-fluid' alt='' />
        }
    }

    const nextPhoto = () => {
        if (dogDetail?.photoIds.length === 1) {
            return <img src={logo} className='h-100 w-100 mx-auto img-fluid img-fluid' alt={'Dog Photo of ' + dogDetail?.name} style={{ paddingTop: '2rem' }} />
        }
        else {
            const index = activeIndex === 0 ? activeIndex + 1 : (activeIndex === dogDetail?.photoIds.length - 1) ? 0 : activeIndex + 1;
            return <img src={id ? (BASE_URL + '/photos/' + dogDetail?.photoIds[index]) : logo} className='h-100 mx-auto img-fluid img-fluid' alt='' />
        }
    }



    const slides = dogDetail?.photoIds.map((id) => {
        return (
            <CarouselItem
                onExiting={() => setAnimating(true)}
                onExited={() => setAnimating(false)}
                key={'photo' + id}
            >
                <div className="hstack h-100 justify-content-center align-items-center gap-3">
                    <img
                        src={id ? (BASE_URL + '/photos/' + id) : noPhoto}
                        className='h-100 w-100'
                        alt=''
                    />
                </div>
            </CarouselItem>
        );
    });

    const lightboxSlides = dogDetail?.photoIds.map((id) => { return { 'src': (BASE_URL + '/photos/' + id), 'width': 1000, 'height': 1000 } });

    return (
        <div >
            {!loading && errorObject &&
                <div className='container-lg'>
                    <ErrorAlert alertType='danger' error={errorObject} showAlert={true} />
                </div>
            }
            {!loading && dogDetail &&
                <section id="dog-detail">
                    <div ref={headerRef}>
                        <div className='row g-0 '>
                            <div className="container-lg px-0">
                                <div className="row g-0 bg-black position-relative" id="dog-pictures-container"
                                    ref={carouselRef}
                                >
                                    <div className='d-none d-lg-block container-lg row g-0 position-absolute'
                                    //onMouseEnter={() => setShowCarouselIndicators(true)}
                                    //onMouseLeave={() => setShowCarouselIndicators(false)}
                                    >
                                        <div className='carousel-item d-block'>
                                            <div style={{ position: 'absolute', left: '0px', right: '0', width: '30%', height: '100%', textAlign: 'center' }}>
                                                {previousPhoto()}
                                            </div>
                                            <div style={{ position: 'absolute', right: '0px', width: '30%', height: '100%', textAlign: 'center' }}>
                                                {nextPhoto()}
                                            </div>
                                        </div>

                                    </div>
                                    {dogDetail.photoIds[0] !== 0 &&
                                        <a onClick={() => setOpenLightbox(true)}
                                            className='open-lightbox-btn h4 col-auto'
                                        >
                                            <i className="bi bi-fullscreen text-light"></i>
                                        </a>
                                    }
                                    <Carousel
                                        activeIndex={activeIndex}
                                        next={next}
                                        previous={previous}
                                        interval={false}
                                        className='px-0'
                                    >
                                        <CarouselIndicators
                                            id='dogCarousel'
                                            items={dogPhotos}
                                            activeIndex={activeIndex}
                                            onClickHandler={goToIndex}
                                            className={classNames('dog-carousel-indicators',
                                                { 'single-image': dogDetail?.photoIds.length === 1 }
                                            )}
                                        />
                                        {slides}
                                        <CarouselControl direction="prev"
                                            directionText="Previous"
                                            onClickHandler={previous}
                                            className={classNames(
                                                {
                                                    'd-none': dogDetail?.photoIds.length === 1,
                                                    'd-flex': dogDetail?.photoIds.length > 1
                                                }
                                            )}
                                        />
                                        <CarouselControl direction="next"
                                            directionText="Next"
                                            onClickHandler={next}
                                            className={classNames(
                                                {
                                                    'd-none': dogDetail?.photoIds.length === 1,
                                                    'd-flex': dogDetail?.photoIds.length > 1
                                                }
                                            )}
                                        />
                                    </Carousel>
                                </div>
                                <Lightbox
                                    open={openLightbox}
                                    close={() => setOpenLightbox(false)}
                                    slides={lightboxSlides}
                                    index={activeIndex}
                                    on={{ view: ({ index: currentIndex }) => setActiveIndex(currentIndex) }}
                                    plugins={[Zoom]}
                                    zoom={{
                                        scrollToZoom: true,
                                        maxZoomPixelRatio: 50
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="container-lg px-0">
                        <div className="row mt-3">
                            <div className="col-lg-6" id="dogDetail" ref={aboutRef}>
                                <div className="box card dog-info pb-0 px-0 px-lg-3 shadow" ref={dogRef}>
                                    <div className="icon">
                                        <img src={dogIcon} alt='' />
                                    </div>
                                    <div className="card-body text-center text-lg-start px-0 px-lg-4 pb-0">
                                        <h1 className="dog-title">{dogDetail?.name}</h1>
                                        <h6 className="pb-3 px-2">
                                            <span className="dog-breed">{dogDetail?.primaryBreed + (dogDetail?.secondaryBreed ? ' Mix' : '')}</span><span className="mx-2">&bull;</span>
                                            <span className="dog-inst">{capitalizeEveryWord(dogDetail?.institution.name)}</span>
                                        </h6>
                                        <h6 className="pb-3 mb-0 px-2">
                                            <span className="dog-age">{dogDetail?.age}</span><span className="mx-2">&bull;</span>
                                            <span className="dog-gender">{dogDetail?.gender}</span><span className="mx-2">&bull;</span>
                                            <span className="dog-size">{dogDetail?.size.substring(0, dogDetail?.size.indexOf('('))}</span><span className="mx-2">&bull;</span>
                                            <span className="dog-color">{dogDetail?.color}</span>
                                        </h6>
                                        <div className="text-start d-none d-lg-block border-top">
                                            <h3 className="card-title mb-4 mt-2">About</h3>
                                            <h5 className="dog-title mb-2">Primary Breed</h5>
                                            <h6 className="mb-4 dog-primary-breed">{dogDetail?.primaryBreed}</h6>
                                            <h5 className="dog-title mb-2">Secondary Breed</h5>
                                            <h6 className="mb-4 dog-secondary-breed">{dogDetail?.secondaryBreed ? dogDetail?.secondaryBreed : "None"}</h6>
                                            <h5 className="dog-title mb-2">Age</h5>
                                            <h6 className="mb-4 dog-age-full">{dogDetail?.yearsMonths}</h6>
                                            <h5 className="dog-title mb-2">Size</h5>
                                            <h6 className="mb-4 dog-size">{dogDetail?.size}</h6>
                                            <h5 className="dog-title mb-2">Active Offenses</h5>
                                            <h6 className="mb-4">
                                                <span className="dog-active-offenses">{dogDetail?.activeOffenses ? dogDetail?.activeOffenses : 'None'}</span>
                                            </h6>
                                            <h5 className="dog-title mb-2">Progress Report</h5>
                                            <h6>
                                                <span className="dog-active-offenses">{dogDetail?.progress ? dogDetail?.progress : 'None'}</span>
                                            </h6>
                                        </div>
                                        <div className={classNames("text-start d-lg-none pt-2 w-100 border-top",
                                            { 'stick': stickyHeader }
                                        )} id="dogDetailBtnRow">
                                            <nav className="navbar">
                                                <ul className="nav">
                                                    <li className="nav-item">
                                                        <div className={classNames("nav-link",
                                                            { 'active': aboutLinkActive })}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={_ => dogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                        >
                                                            About
                                                        </div>
                                                    </li>
                                                    <li className="nav-item">
                                                        <div className={classNames("nav-link",
                                                            { 'active': shelterLinkActive })} style={{ cursor: 'pointer' }}
                                                            onClick={_ => shelterRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                        >
                                                            Shelter
                                                        </div>
                                                    </li>
                                                    <li className="nav-item">
                                                        <div className={classNames("nav-link",
                                                            { 'active': institutionLinkActive })} style={{ cursor: 'pointer' }}
                                                            onClick={_ => institutionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                        >
                                                            Institution
                                                        </div>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                                <div id="dog-bio" className="d-lg-none">
                                    <div className='card dog-info shadow'>
                                        <div className='card-body'>
                                            <h3 className="card-title mb-4 mt-2">About</h3>
                                            <h5 className="dog-title mb-2">Primary Breed</h5>
                                            <h6 className="mb-4 dog-primary-breed">{dogDetail?.primaryBreed}</h6>
                                            <h5 className="dog-title mb-2">Secondary Breed</h5>
                                            <h6 className="mb-4 dog-secondary-breed">{dogDetail?.secondaryBreed !== "0" ? dogDetail?.secondaryBreed : "None"}</h6>
                                            <h5 className="dog-title mb-2">Age</h5>
                                            <h6 className="mb-4 dog-age-full">{dogDetail?.yearsMonths}</h6>
                                            <h5 className="dog-title mb-2">Size</h5>
                                            <h6 className="mb-4 dog-size">{dogDetail?.size}</h6>
                                            <h5 className="dog-title mb-2">Active Offenses</h5>
                                            <h6 className="mb-4">
                                                <span className="dog-active-offenses">{dogDetail?.activeOffenses ? dogDetail?.activeOffenses : 'None'}</span>
                                            </h6>
                                            <h5 className="dog-title mb-2">Progress Report</h5>
                                            <h6>
                                                <span className="dog-active-offenses">{dogDetail?.progress ? dogDetail?.progress : 'None'}</span>
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 mt-5 mt-lg-0" ref={shelterRef}>
                                <div id="shelter">
                                    <div className="box card shadow">
                                        <div className="icon">
                                            <img src={shelterIcon} alt='' />
                                        </div>
                                        <div className="title text-center border-bottom pb-3 px-3">
                                            <h4 className="dog-shelter h4">{dogDetail?.shelter?.name}</h4>
                                            <h6 className="dog-shelter-city h6">{dogDetail?.shelter?.cityStateZip}</h6>
                                        </div>
                                        <div className="d-flex py-3">
                                            <div className="col-2 text-center">
                                                <i className="bi bi-geo-alt-fill h4"></i>
                                            </div>
                                            <div className="col-10 pb-3 border-bottom">
                                                <div className="fw-bold">Location Address</div>
                                                <span className="dog-shelter-address">{capitalizeEveryWord(dogDetail?.shelter?.addressOne, 'PO')}<br />
                                                    {dogDetail?.shelter?.cityStateZip}</span>
                                            </div>
                                        </div>
                                        <div className="d-flex">
                                            <div className="col-2 text-center">
                                                <i className="bi bi-box-arrow-right h4"></i>
                                            </div>
                                            <div className="col-10 pb-3 border-bottom">
                                                <a className="dog-shelter-email" href={dogDetail?.shelter?.url && 'https://' + dogDetail?.shelter?.url}>{dogDetail?.shelter?.url} </a>
                                            </div>
                                        </div>
                                        <div className="d-flex py-3">
                                            <div className="col-2 text-center">
                                                <i className="bi bi-telephone-fill h4"></i>
                                            </div>
                                            <div className="col-10 pb-3 border-bottom">
                                                <div className="dog-shelter-contact">{dogDetail?.shelter?.phone}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div id="institution" className="pb-5" ref={institutionRef} style={{ marginBottom: '160px' }}>
                                    <div className="box card shadow">
                                        <div className="icon">
                                            <img src={instIcon} alt='' />
                                        </div>
                                        <div className="title text-center border-bottom pb-3 px-3">
                                            <h4 className="dog-shelter h4">{dogDetail?.institution?.name}</h4>
                                            <h6 className="dog-shelter-city h6">{capitalizeEveryWord(dogDetail?.institution?.cityStateZip, 'MO')}</h6>
                                        </div>
                                        <div className="d-flex py-3">
                                            <div className="col-2 text-center">
                                                <i className="bi bi-geo-alt-fill h4"></i>
                                            </div>
                                            <div className="col-10 pb-3 border-bottom">
                                                <div className="fw-bold">Location Address</div>
                                                <span className="dog-shelter-address">{capitalizeEveryWord(dogDetail?.institution?.addressOne, 'PO')}<br />
                                                    {dogDetail?.institution?.cityStateZip}</span>
                                            </div>
                                        </div>
                                        {dogDetail?.institution?.phone &&
                                            <div className="d-flex py-3">
                                                <div className="col-2 text-center">
                                                    <i className="bi bi-telephone-fill h4"></i>
                                                </div>
                                                <div className="col-10 pb-3 border-bottom">
                                                    <div className="fw-bold">Coordinator</div>
                                                    {dogDetail?.institution?.coordinatorName &&
                                                        <div>{dogDetail?.institution?.coordinatorName ? capitalizeEveryWord(dogDetail?.institution?.coordinatorName) : 'None'}</div>
                                                    }
                                                    <div>{dogDetail?.institution?.phone}</div>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section >
            }
        </div>


    )
}

export default DogDetail;